/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import type { TCarouselItem } from "../../model/types";
import type { CarouselTemplate } from "../../model/enums";

/**
 * ViewModel state and presentation-ready shapes for the Shows & Movies feature
 * (Figma: Shows & Movies Detail — 10 foot page).
 *
 * The screen is a two-strip catalogue browser: a top strip that picks the
 * catalogue ("For You", "On Demand", "Shows", "Movies", "TiVo+"), an optional
 * genre strip below it, and a stack of carousel rails underneath both.
 */

/**
 * Identifiers for the top-level catalogue tabs.
 *
 * Values match the final segment of the Showflix `/screens/{id}` endpoint.
 */
export enum ShowsAndMoviesFilterId {
  FOR_YOU = "forYou",
  ON_DEMAND = "onDemand",
  SHOWS = "shows",
  MOVIES = "movies",
  TIVO_PLUS = "tivoPlus"
}

/**
 * Identifiers for the genre sub-filter chips rendered under the tab strip
 * (Figma: "Selection - SubFilters View").
 *
 * Only the catalogue tabs that carry genres show this row — see
 * {@link hasGenreSubFilters}.
 */
export enum ShowsAndMoviesGenreId {
  ALL = "all",
  ACTION = "action",
  DRAMA = "drama",
  REALITY = "reality",
  COMEDY = "comedy",
  KIDS = "kids"
}

/**
 * D-pad / pointer focus region for the Shows & Movies screen. The active region
 * decides which strip or carousel row receives directional input routed from
 * the ScreenController.
 */
export enum ShowsAndMoviesFocusRegion {
  FILTERS = "filters",
  GENRES = "genres",
  CAROUSEL_ITEMS = "carouselItems"
}

/** Metadata for a single API-provided catalogue tab. */
export interface TShowsAndMoviesFilterMeta {
  id: ShowsAndMoviesFilterId;
  label: string;
  /** Server-selected composition for this Showflix screen. */
  templateType?: TShowsAndMoviesScreenTemplateType;
  /** Whether the server marks this screen as the initial tab. */
  isDefault?: boolean;
}

export const ShowsAndMoviesScreenTemplateType = {
  SINGLE_CAROUSEL_GRID: "SINGLE_CAROUSEL_GRID",
  MULTI_CAROUSEL_ROWS: "MULTI_CAROUSEL_ROWS"
} as const;

export type TShowsAndMoviesScreenTemplateType =
  (typeof ShowsAndMoviesScreenTemplateType)[keyof typeof ShowsAndMoviesScreenTemplateType];

/** Metadata for a single API-provided genre sub-filter chip. */
export interface TShowsAndMoviesGenreMeta {
  id: ShowsAndMoviesGenreId;
  label: string;
  /** Opaque Showflix genreId passed back to the screenGet API. */
  apiGenreId: string;
}

/** One presentation-ready carousel rail for the Shows & Movies view layer. */
export interface TShowsAndMoviesCarouselData {
  /** Opaque, server-driven carousel id — used for paging and as the React key. */
  carouselId: string;
  /** Localized display label ("Recommended For You", "Highlights", ...). */
  title: string;
  /** Bounded render hint for the tile shape within the carousel row. */
  layout: CarouselTemplate;
  /** True carousel length — drives See More paging. Undefined when unknown. */
  resolvedCount: number | undefined;
  items: TCarouselItem[];
}

/** Fetch state for the screen's carousel rails. */
export interface TShowsAndMoviesCarouselState {
  items: TShowsAndMoviesCarouselData[];
  /** Cursor returned by the screen API for the next vertical carousel page. */
  pageCursor: string | null;
  refreshIntervalSecs: number;
  lastFetchTimestamp: number;
  lastFetchedCatalogueKey: string;
  isLoading: boolean;
  isLoadingMore: boolean;
  /** Carousel IDs with an offset page currently in flight. */
  loadingCarouselIds: string[];
  error: string;
  /** requestId of the fetch/loadMore currently owning this surface, or null. */
  requestId: string | null;
}

/** Redux state for the Shows & Movies screen. */
export interface ShowsAndMoviesState {
  selectedFilter: ShowsAndMoviesFilterId;
  selectedGenres: ShowsAndMoviesGenreId[];
  filterOptions: TShowsAndMoviesFilterMeta[];
  genreOptions: TShowsAndMoviesGenreMeta[];
  carousels: TShowsAndMoviesCarouselState;
  focusRegion: ShowsAndMoviesFocusRegion;
  focusedFilterIndex: number;
  focusedGenreIndex: number;
  focusedCarouselIndex: number;
  focusedItemIndex: number;
  /** Per-row tile index remembered while focus is on another row. */
  carouselScrollPositions: number[];
  /** Focusable slot count per row, synced from the view — navigation bounds. */
  carouselItemCounts: number[];
  /**
   * Set when `/screens` fails outright, or succeeds with no usable tabs
   * (For You, On Demand, Shows, Movies, TiVo+) to render. Empty otherwise.
   */
  navigationError: string;
}
